import { useEffect, useState, useCallback } from "react";
import { Plus, Search, MoreVertical, Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatRupiah, formatDateShort, formatApiError, todayStr } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import ConfirmDialog from "@/components/ConfirmDialog";
import DataPagination from "@/components/DataPagination";
import StatCard from "@/components/StatCard";
import { TableSkeleton } from "@/components/Skeletons";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const FinancePage = ({ title, subtitle, endpoint, categories, icon, accent = "primary", totalLabel, enableProductPicker = false }) => {
  const blank = { name: "", nominal: 0, category: categories[0] || "", date: todayStr(), note: "", product_id: null, qty: null, unit_price: null };
  const [data, setData] = useState({ items: [], total: 0, total_nominal: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [delId, setDelId] = useState(null);
  const [products, setProducts] = useState([]);
  const [qty, setQty] = useState(1);
  const [unitPrice, setUnitPrice] = useState(0);

  useEffect(() => {
    if (enableProductPicker) {
      api.get("/products", { params: { limit: 1000, status: "active" } })
        .then((r) => setProducts(r.data.items))
        .catch(() => {});
    }
  }, [enableProductPicker]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(endpoint, { params: { search, category, page, limit: 8 } });
      setData(data);
    } finally {
      setLoading(false);
    }
  }, [endpoint, search, category, page]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const openNew = () => { setEditing(null); setForm(blank); setQty(1); setUnitPrice(0); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...blank, ...it }); setQty(1); setUnitPrice(0); setOpen(true); };

  const pickProduct = (pid) => {
    const p = products.find((x) => x.id === pid);
    if (!p) return;
    setUnitPrice(p.sell_price);
    setQty(1);
    setForm((f) => ({ ...f, name: p.name, nominal: p.sell_price }));
  };
  const changeQty = (q) => {
    setQty(q);
    setForm((f) => ({ ...f, nominal: (unitPrice || 0) * Number(q || 0) }));
  };

  const save = async () => {
    if (!form.name) { toast.error("Nama wajib diisi"); return; }
    setSaving(true);
    try {
      const payload = { ...form, nominal: Number(form.nominal) };
      if (editing) await api.put(`${endpoint}/${editing.id}`, payload);
      else await api.post(endpoint, payload);
      toast.success(editing ? "Data diperbarui" : "Data ditambahkan");
      setOpen(false);
      if (!editing) setPage(1);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    try { await api.delete(`${endpoint}/${delId}`); toast.success("Data dihapus"); load(); }
    finally { setDelId(null); }
  };

  return (
    <div className="space-y-6">
      <PageHeader title={title} subtitle={subtitle}>
        <Button onClick={openNew} data-testid={`add-${endpoint.replace("/", "")}-btn`}>
          <Plus className="mr-2 h-4 w-4" /> Tambah
        </Button>
      </PageHeader>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
        <StatCard testId={`total-${endpoint.replace("/", "")}`} title={totalLabel} value={formatRupiah(data.total_nominal)} icon={icon} accent={accent} />
      </div>

      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Cari..." className="pl-9" value={search} data-testid="finance-search-input" onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
        </div>
        <Select value={category} onValueChange={(v) => { setCategory(v); setPage(1); }}>
          <SelectTrigger className="w-full sm:w-52" data-testid="finance-category-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Kategori</SelectItem>
            {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-xl border border-border bg-card p-2 shadow-[0_2px_10px_rgba(0,0,0,0.03)]">
        {loading ? (
          <div className="p-4"><TableSkeleton cols={5} /></div>
        ) : data.items.length === 0 ? (
          <EmptyState icon={icon} title="Belum ada data" description="Mulai catat data untuk melihat statistik." action={<Button onClick={openNew}><Plus className="mr-2 h-4 w-4" />Tambah</Button>} />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nama</TableHead>
                <TableHead>Kategori</TableHead>
                {enableProductPicker && <TableHead className="text-center">Qty</TableHead>}
                {enableProductPicker && <TableHead className="text-right">Harga Satuan</TableHead>}
                <TableHead>Tanggal</TableHead>
                <TableHead className="text-right">Nominal</TableHead>
                <TableHead className="w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((it) => (
                <TableRow key={it.id} data-testid={`finance-row-${it.id}`}>
                  <TableCell>
                    <p className="font-medium text-foreground">{it.name}</p>
                    {it.note && <p className="text-xs text-muted-foreground">{it.note}</p>}
                  </TableCell>
                  <TableCell><span className="rounded-full bg-secondary px-2.5 py-0.5 text-xs font-medium">{it.category || "-"}</span></TableCell>
                  {enableProductPicker && <TableCell className="text-center">{it.qty ? it.qty : "-"}</TableCell>}
                  {enableProductPicker && <TableCell className="text-right">{it.unit_price ? formatRupiah(it.unit_price) : "-"}</TableCell>}
                  <TableCell>{formatDateShort(it.date)}</TableCell>
                  <TableCell className="text-right font-semibold">{formatRupiah(it.nominal)}</TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" data-testid={`finance-actions-${it.id}`}><MoreVertical className="h-4 w-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEdit(it)}><Pencil className="mr-2 h-4 w-4" />Edit</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setDelId(it.id)} className="text-destructive"><Trash2 className="mr-2 h-4 w-4" />Hapus</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {!loading && <DataPagination page={page} limit={8} total={data.total} onPage={setPage} />}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>{editing ? `Edit ${title}` : `Tambah ${title}`}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {enableProductPicker && (
              <div className="grid grid-cols-3 gap-3 rounded-lg border border-dashed border-border p-3">
                <div className="col-span-2 space-y-2">
                  <Label>Dari Produk/Jasa (opsional)</Label>
                  <Select onValueChange={pickProduct}>
                    <SelectTrigger data-testid="income-product-select"><SelectValue placeholder="Pilih produk/jasa" /></SelectTrigger>
                    <SelectContent>
                      {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {formatRupiah(p.sell_price)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Qty</Label>
                  <Input type="number" min="1" value={qty} onChange={(e) => changeQty(e.target.value)} data-testid="income-qty-input" />
                </div>
              </div>
            )}
            <div className="space-y-2"><Label>Nama *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="finance-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Kategori</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="finance-category-input"><SelectValue /></SelectTrigger>
                  <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Tanggal</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
            </div>
            <div className="space-y-2"><Label>Nominal (Rp)</Label><Input type="number" value={form.nominal} onChange={(e) => setForm({ ...form, nominal: e.target.value })} data-testid="finance-nominal-input" /></div>
            <div className="space-y-2"><Label>Keterangan</Label><Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={save} disabled={saving} data-testid="save-finance-btn">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!delId} onOpenChange={() => setDelId(null)} title="Hapus Data?" description="Tindakan ini tidak dapat dibatalkan." onConfirm={doDelete} />
    </div>
  );
};

export default FinancePage;
